import React, { useCallback, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { debounce } from '@mui/material/utils';

import useCurrentBrand from '~/hooks/useCurrentBrand';

import { getBusinessList } from '~/services/BusinessTraveller/BusinessTravellerService';

interface Props {
  value: App.Business | null;
  onChange: (business: App.Business | null) => void;
}

export default function BusinessSearchWidget({ value, onChange }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const brand = useCurrentBrand();
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const debouncedSearch = useCallback(
    debounce((val: string) => setSearchQuery(val), 500),
    [],
  );

  const { data: options = [], isLoading } = useQuery({
    queryKey: ['businesses', brand, searchQuery],
    queryFn: async (): Promise<Array<App.Business>> => {
      try {
        const response = await getBusinessList(20, 0, searchQuery);
        return response.result;
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          enqueueSnackbar('Error fetching businesses', { variant: 'error' });
        }
        throw e;
      }
    },
    enabled: searchQuery.length >= 2,
    staleTime: 60_000,
    gcTime: 300_000,
  });

  return (
    <Autocomplete
      options={options}
      value={value}
      onChange={(_, newValue) => onChange(typeof newValue === 'string' ? null : newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInput) => {
        setInputValue(newInput);
        debouncedSearch(newInput);
      }}
      getOptionLabel={(option) => option?.name ?? ''}
      isOptionEqualToValue={(a, b) => a?.id === b?.id}
      filterOptions={(x) => x}
      loading={isLoading}
      noOptionsText="No businesses"
      loadingText="Loading..."
      renderInput={(params) => (
        <TextField
          {...params}
          label="Business"
          placeholder="Search Business"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
